import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import {
  FaLock,
  FaEye,
  FaEyeSlash,
  FaShieldAlt,
  FaArrowLeft,
  FaCheckCircle,
} from "react-icons/fa";
import { logoutUser, selectUser } from "../../features/userSlice";
import logo from "../../assets/grouplogo.png";
import "./ChangePassword.css";

const ChangePassword = () => {
  const user = useSelector(selectUser);
  const emp_id = user?.emp_id;
  const token = user?.token;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate("/login");
    }
  }, [token, navigate]);

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!oldPassword || !newPassword || !confirmPassword) {
      setError("Please fill all fields.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match.");
      return;
    }

    if (oldPassword === newPassword) {
      setError("New password must be different from old password.");
      return;
    }

    setError("");
    setMessage("");
    setLoading(true);

    try {
      await axios.post(
        "/api/Users/changePassword",
        { emp_id, oldPassword, newPassword },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setMessage("Password updated successfully. Please sign in again.");
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");

      setTimeout(() => {
        dispatch(logoutUser());
        navigate("/login");
      }, 2200);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to change password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cp-page">
      <div className="cp-bg" aria-hidden="true" />

      <main className="cp-shell">
        <section className="cp-panel">
          <div className="cp-brand">
            <img src={logo} alt="Singhal Logistics" className="cp-logo" />
            <p className="cp-brand-name">Singhal Logistics</p>
            <p className="cp-brand-sub">Yard & Weighbridge Operations</p>
          </div>

          <div className="cp-title-row">
            <span className="cp-title-icon" aria-hidden="true">
              <FaShieldAlt />
            </span>
            <div>
              <h1 className="cp-title">Change Password</h1>
              <p className="cp-support">
                Update your account password to keep access secure.
              </p>
            </div>
          </div>

          {error ? (
            <div className="cp-alert cp-alert-error" role="alert">
              {error}
            </div>
          ) : null}

          {message ? (
            <div className="cp-alert cp-alert-success" role="status">
              <FaCheckCircle aria-hidden="true" />
              <span>{message}</span>
            </div>
          ) : null}

          <form onSubmit={submitHandler} className="cp-form" noValidate>
            <label className="cp-field">
              <span>Old Password</span>
              <div className="cp-input-wrap">
                <FaLock className="cp-input-icon" aria-hidden="true" />
                <input
                  type={showOld ? "text" : "password"}
                  value={oldPassword}
                  placeholder="Enter current password"
                  onChange={(e) => setOldPassword(e.target.value)}
                  autoComplete="current-password"
                  disabled={loading || !!message}
                  required
                />
                <button
                  type="button"
                  className="cp-eye-btn"
                  onClick={() => setShowOld((v) => !v)}
                  aria-label={showOld ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showOld ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </label>

            <label className="cp-field">
              <span>New Password</span>
              <div className="cp-input-wrap">
                <FaLock className="cp-input-icon" aria-hidden="true" />
                <input
                  type={showNew ? "text" : "password"}
                  value={newPassword}
                  placeholder="Enter new password"
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  disabled={loading || !!message}
                  required
                />
                <button
                  type="button"
                  className="cp-eye-btn"
                  onClick={() => setShowNew((v) => !v)}
                  aria-label={showNew ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showNew ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </label>

            <label className="cp-field">
              <span>Confirm New Password</span>
              <div className="cp-input-wrap">
                <FaLock className="cp-input-icon" aria-hidden="true" />
                <input
                  type={showConfirm ? "text" : "password"}
                  value={confirmPassword}
                  placeholder="Re-enter new password"
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  disabled={loading || !!message}
                  required
                />
                <button
                  type="button"
                  className="cp-eye-btn"
                  onClick={() => setShowConfirm((v) => !v)}
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showConfirm ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </label>

            <button className="cp-submit" type="submit" disabled={loading || !!message}>
              {loading ? "Updating..." : "Update Password"}
            </button>

            <button
              type="button"
              className="cp-back"
              onClick={() => navigate("/dashboard")}
              disabled={loading}
            >
              <FaArrowLeft aria-hidden="true" /> Back to Dashboard
            </button>
          </form>
        </section>
      </main>

      
    </div>
  );
};

export default ChangePassword;
